// THE BULWARK CUBE on screen (sim/bosses/bulwark.ts). A regiment of pot-belly cast-iron stoves
// (tools/boss_forge/bulwark_cube.py: mauve iron, brass trim, one slit visor, a firebox grille, a
// round-topped riot door). 36 soldiers drawn as ONE InstancedMesh per model part (the sergeant GLB
// has nine), so the whole regiment costs ~9 draws however many stand. The BANNERMAN is the
// burnt-orange one: whoever carries the banner is drawn from the bannerman GLB instead (its own
// small instanced set: him + his corpse), pennant, pilot cage and gold pilot light included.
// Wired by part NAME (body, head, leg_L/R, arm_shield > shield_slab, arm_free, fx_*; banner,
// pilot_cage, wp_banner_core), so a remodelled GLB with the same names drops straight in
// (Assets/bosses/bulwark_*/, packed into public/assets/run/ by pack_glb.mjs).
//
// What reads, from across the arena:
//   - drop-in: the regiment rains down a column at a time and lands with a thump
//   - marching: synchronized hop, legs swing; slabs turn to face their target
//   - drill call (the tell): every soldier crouches and stamps, the bar names the next formation
//   - parade rest: the doors swing open on their hinges (SLAB_ / APRON_HINGE + _SWING, as the server's
//     hit test): the stove fronts show
//   - slam: the crouch, then a red ring rolls out from the cube (jump it)
//   - ceiling drop: strike rings fill on the ground where the top row will land
//   - the bannerman: the orange stove with a tall red pennant + a throbbing GOLD pilot light on
//     his back; when he falls the pennant flies to the next in line (G5) and, in the flash on
//     arrival, the new bearer takes the orange
//   - burning soldiers tumble out of the ranks (stop, drop and roll)

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { BossBeat, BossKind } from '../../../../sim/bosses/types';
import { CPart, CubeBit, CubeEv, CubeNet, Cs, DropSt, FORMATION_NAMES, Fm, PART_ARMOR, SLAB_HINGE, SLAB_SWING, APRON_HINGE, APRON_SWING, SLAM, unitRaycast } from '../../../../sim/bosses/bulwark';
import { EFlag } from '../../../../sim/protocol';
import { withHaze } from '../../materials';
import type { HudBoss, HudObjective } from '../../contracts';
import { DANGER, Decal, groundDecal } from './decals';
import type { BossAim, BossClient, BossClientFactory, BossCtx, BossFrame } from './types';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const SERGEANT = BASE + 'assets/run/bulwark_sergeant.glb';
const BANNERMAN = BASE + 'assets/run/bulwark_bannerman.glb';
const MAX = 36;
/** fallen soldiers still toppling / sinking (drawn as extra instances of the same meshes) */
const CORPSES = 12, TOPPLE = 0.55, LIE = 0.9, SINK = 0.8;
const DROP_H = 60;
/** the bannerman set holds the bearer + his corpse */
const BANNER_CAP = 2;
const DRILL_ADVICE: Record<number, string> = {
  [Fm.Advance]: 'FLANK IT OR GET ABOVE IT', [Fm.Encircle]: 'GET OUTSIDE THE RING BEFORE IT CLOSES',
  [Fm.Grinder]: 'BACK OFF: THE SLABS ARE ABOUT TO SPIN', [Fm.Gauntlet]: 'GET OUT OF ITS LANE', [Fm.Drop]: 'KEEP MOVING: THE TOP ROW IS COMING DOWN',
};
/** a stable 0..1 per soldier id (and salt) */
function hash01(id: number, salt: number) {
  const v = Math.sin(id * 12.9898 + salt * 78.233) * 43758.5453;
  return v - Math.floor(v);
}
/** each stove's own paint: mauve a shade lighter/darker/pinker/greyer, about one in four rustier */
function unitTint(id: number, c: THREE.Color) {
  const v = 0.9 + 0.18 * hash01(id, 5), hue = hash01(id, 6) - 0.5;
  if (hash01(id, 7) < 0.25) c.setRGB(v * 1.08, v * 0.94, v * 0.84);
  else c.setRGB(v * (1 + 0.06 * hue), v, v * (1 - 0.05 * hue));
}
/** swing a hinged leaf by ang rad about its hinge (d = pivot -> hinge, model x/z; three.js rotation.y) */
function swing(o: THREE.Object3D | null, d: THREE.Vector3, ang: number) {
  if (!o) return;
  const c = Math.cos(ang), sn = Math.sin(ang);
  o.position.x += d.x - (d.x * c + d.z * sn);
  o.position.z += d.z - (-d.x * sn + d.z * c);
  o.rotation.y += ang;
}
const PART_NAME = ['', 'STOVE-DOOR SLAB · AP5', 'FIREBOX', 'DOME · AV2', 'FLUE · CRIT', 'PILOT LIGHT · CRIT'];

interface Unit { x: number; y: number; z: number; yaw: number; walk: number; seen: number; bearer: boolean }
interface Corpse { x: number; y: number; z: number; yaw: number; t0: number; side: number; bearer: boolean }
/** one model drawn instanced: its posable template (node tree), rest pose and an InstancedMesh per mesh */
interface Kit {
  T: THREE.Object3D;
  N: Record<string, THREE.Object3D | null>;
  rest: Map<THREE.Object3D, { p: THREE.Vector3; r: THREE.Euler }>;
  /** the door, and the hinge's offset from its pivot (model space) for the parade-rest swing */
  slab: THREE.Object3D | null;
  slabD: THREE.Vector3;
  /** the stove's own firebox door (the gun side's cover), swung like the slab at parade rest */
  door: THREE.Object3D | null;
  doorD: THREE.Vector3;
  parts: { src: THREE.Mesh; inst: THREE.InstancedMesh; extra: boolean }[];
  cap: number;
  n: number;
}

// The regiment's look is baked into each GLB's texture atlas (texkit: mauve cast iron, brass,
// rust washes, stickers; the bannerman's iron is burnt orange), so the client draws the
// materials as they come: no vertex re-inking. The glow_* materials (visor, firebox, flue, gold
// pilot light) stay emissive, so they hold their heat through the haze.
const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);

class CubeClient implements BossClient {
  readonly kind = BossKind.BulwarkCube;
  readonly title = 'THE BULWARK CUBE';
  readonly sub = 'THE HOME GUARD';
  /** the regiment (sergeant GLB) and the bearer (bannerman GLB) */
  private kit: Kit | null = null;
  private bkit: Kit | null = null;
  private coreHolder: THREE.Object3D | null = null;
  private bannerFly: THREE.Mesh | null = null;
  private glow: THREE.Sprite;
  private flashSprite: THREE.Sprite;
  private ring: Decal;
  private strikes: Decal[] = [];
  private units = new Map<number, Unit>();
  private corpses: Corpse[] = [];
  private ground: (x: number, z: number) => number = () => 0;
  private col = new THREE.Color();
  private v1 = new THREE.Vector3();
  private disposables: { dispose(): void }[] = [];
  private lastBearer = 0;
  private bearerPos = new THREE.Vector3();
  private fly: { from: THREE.Vector3; to: number; t: number } | null = null;
  private arriveT = -1;
  private part = { t: -1, part: CPart.None };
  private time = 0;

  constructor(private ctx: BossCtx) {
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    Promise.all([loader.loadAsync(SERGEANT), loader.loadAsync(BANNERMAN)])
      .then(([a, b]) => {
        this.onModels(a.scene, b.scene);
        const warm: THREE.Mesh[] = [...(this.kit?.parts ?? []), ...(this.bkit?.parts ?? [])].map((p) => p.inst);
        if (this.bannerFly) warm.push(this.bannerFly);
        ctx.prewarm?.(warm);
      })
      .catch((e) => console.warn('[bulwark] models failed', e));
    const tex = glowTex();
    this.disposables.push(tex);
    const gm = new THREE.SpriteMaterial({ map: tex, color: 0xffc34a, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 });
    this.glow = new THREE.Sprite(gm); this.glow.scale.setScalar(1.3); this.glow.renderOrder = 46; this.glow.visible = false;
    const fm = new THREE.SpriteMaterial({ map: tex, color: 0xffe08a, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 });
    this.flashSprite = new THREE.Sprite(fm); this.flashSprite.renderOrder = 46; this.flashSprite.visible = false;
    this.disposables.push(gm, fm);
    ctx.scene.add(this.glow, this.flashSprite);
    this.ring = groundDecal(ctx.scene, ctx.time, 1, DANGER);
    for (let i = 0; i < 4; i++) this.strikes.push(groundDecal(ctx.scene, ctx.time, 3, DANGER));
  }

  private onModels(sergeant: THREE.Object3D, banner: THREE.Object3D) {
    const mats = new Map<THREE.Material, THREE.Material>();
    const prep = (m: THREE.Material) => {
      let o = mats.get(m);
      // light haze only: the regiment is the subject of the arena, it should hold its colour at 40 m
      if (!o) {
        const glowing = m.name.startsWith('glow');
        o = withHaze(m, m.name === 'paint' ? 0.16 : glowing ? 0.04 : 0.12);
        const sm = o as THREE.MeshStandardMaterial;
        // visors + fireboxes punch through the arena haze (ref_02/03's rows of hot slits)
        if (glowing && sm.emissiveIntensity !== undefined) sm.emissiveIntensity *= 1.8;
        // a little of the printed atlas glows through the shade side: the slabs face the posse, so
        // the camera mostly sees their unlit side, and the mauve must not sink to brown there
        if (m.name === 'paint' && sm.map) { sm.emissive.setRGB(1, 1, 1); sm.emissiveMap = sm.map; sm.emissiveIntensity = 0.55; }
        mats.set(m, o); this.disposables.push(o);
      }
      return o;
    };
    this.kit = this.makeKit(sergeant, MAX + CORPSES, prep);
    this.bkit = this.makeKit(banner, BANNER_CAP, prep);
    this.coreHolder = banner.getObjectByName('wp_banner_core') ?? null;
    const src = banner.getObjectByName('banner') as THREE.Mesh | undefined;
    if (src?.isMesh) {
      this.bannerFly = new THREE.Mesh(src.geometry, prep(src.material as THREE.Material));
      this.bannerFly.visible = false;
      this.ctx.scene.add(this.bannerFly);
    }
  }

  private makeKit(T: THREE.Object3D, cap: number, prep: (m: THREE.Material) => THREE.Material): Kit {
    const kit: Kit = { T, N: {}, rest: new Map(), parts: [], cap, n: 0, slab: null, slabD: new THREE.Vector3(), door: null, doorD: new THREE.Vector3() };
    T.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) {
        const m = o as THREE.Mesh;
        const inst = new THREE.InstancedMesh(m.geometry, prep(m.material as THREE.Material), cap);
        inst.castShadow = true; inst.receiveShadow = true; inst.frustumCulled = false; inst.count = 0;
        inst.name = 'cube_' + m.name;
        for (let i = 0; i < cap; i++) inst.setColorAt(i, this.col.setRGB(1, 1, 1));
        this.ctx.scene.add(inst);
        // the bannerman's own kit (pennant, cage, gold light) leaves his corpse
        let extra = false;
        for (let q: THREE.Object3D | null = m; q && q !== T; q = q.parent) if (q.name === 'banner' || q.name === 'pilot_cage' || q.name === 'wp_banner_core') extra = true;
        kit.parts.push({ src: m, inst, extra });
        this.disposables.push(m.geometry);
      }
      if (o !== T) kit.rest.set(o, { p: o.position.clone(), r: o.rotation.clone() });
    });
    for (const n of ['body', 'head', 'leg_L', 'leg_R', 'arm_shield', 'arm_free']) kit.N[n] = T.getObjectByName(n) ?? null;
    // each hinged leaf keeps its pivot -> hinge offset (model x, z) so it can swing about the hinge
    T.updateMatrixWorld(true);
    const g = new THREE.Vector3();
    kit.slab = T.getObjectByName('shield_slab') ?? null;
    if (kit.slab) {
      g.setFromMatrixPosition(kit.slab.matrixWorld);
      kit.slabD.set(SLAB_HINGE[0] - (g.x - T.position.x), 0, SLAB_HINGE[1] - (g.z - T.position.z));
    }
    kit.door = T.getObjectByName('firebox_door') ?? null;
    if (kit.door) {
      g.setFromMatrixPosition(kit.door.matrixWorld);
      kit.doorD.set(APRON_HINGE[0] - (g.x - T.position.x), 0, APRON_HINGE[1] - (g.z - T.position.z));
    }
    return kit;
  }

  update(f: BossFrame | null, time: number, dt: number) {
    this.time = time;
    this.ring.mesh.visible = false;
    if (f) this.ground = f.ground;
    for (const s of this.strikes) s.mesh.visible = false;
    if (!f || !f.n) {
      // nobody standing: only the fallen are left to finish their topple
      this.hide();
      if (this.kit && this.corpses.length) { this.begin(); this.drawCorpses(time); this.end(); }
      return;
    }
    const net = f.net as CubeNet;
    // ---- the banner flying to the next in line (first: a landed banner turns its bearer orange this frame)
    this.bannerman(f, net, time, dt);
    // ---- the regiment, the orange bearer and his gold pilot light
    if (this.kit) this.poseUnits(f, net, time, dt);
    // ---- telegraphs
    const gy = f.ground(net.cx, net.cz);
    if (net.phase & (CubeBit.SlamTell | CubeBit.Ring)) {
      const r = net.phase & CubeBit.Ring ? net.ring : 3.7 + 0.6 * Math.sin(time * 18);
      this.ring.mesh.visible = true;
      this.ring.mesh.position.set(net.cx, gy + 0.1, net.cz);
      this.ring.mesh.scale.set(30, 1, 30);
      this.ring.u.uR.value = Math.min(0.99, r / 15);
    }
    if (net.drop >= DropSt.Tell && net.drop <= DropSt.Fall) {
      // one strike ring per locked cluster, filling toward impact
      const k = net.drop === DropSt.Tell ? net.dropK * 0.6 : 0.6 + 0.4 * ((net.drop - 2) + net.dropK) / 3;
      for (let i = 0; i < net.clusters.length && i < this.strikes.length; i++) {
        const c = net.clusters[i], d = this.strikes[i];
        d.mesh.visible = true;
        d.mesh.position.set(c.x, f.ground(c.x, c.z) + 0.12, c.z);
        d.mesh.scale.set(12.4, 1, 12.4);
        d.u.uK.value = k;
      }
    }
  }

  private hide() {
    for (const k of [this.kit, this.bkit]) if (k) for (const p of k.parts) { p.inst.count = 0; p.inst.visible = false; }
    this.glow.visible = false;
    if (this.bannerFly) this.bannerFly.visible = false;
    this.flashSprite.visible = false;
    this.units.clear();
  }

  private begin() { for (const k of [this.kit, this.bkit]) if (k) k.n = 0; }

  private end() {
    for (const k of [this.kit, this.bkit]) {
      if (!k) continue;
      for (const p of k.parts) {
        p.inst.count = k.n; p.inst.visible = k.n > 0;
        p.inst.instanceMatrix.needsUpdate = true;
        if (p.inst.instanceColor) p.inst.instanceColor.needsUpdate = true;
      }
      k.T.rotation.set(0, 0, 0, 'YXZ');
    }
  }

  /** write the posed template into the kit's next instance slot (bearer's corpse: kit stripped) */
  private emit(k: Kit, stripped = false) {
    if (k.n >= k.cap) return;
    k.T.updateMatrixWorld(true);
    for (const p of k.parts) {
      p.inst.setMatrixAt(k.n, stripped && p.extra ? ZERO : p.src.matrixWorld);
      p.inst.setColorAt(k.n, this.col);
    }
    k.n++;
  }

  /** the fallen: topple onto the slab (or sideways), lie a beat scorched, sink into the ground */
  private drawCorpses(time: number) {
    this.corpses = this.corpses.filter((c) => time - c.t0 < TOPPLE + LIE + SINK);
    for (const c of this.corpses) {
      const k = c.bearer && this.bkit && this.bkit.n < this.bkit.cap ? this.bkit : this.kit!;
      const N = k.N, T = k.T;
      const a = time - c.t0;
      const q = Math.min(1, a / TOPPLE);
      const tip = (Math.PI / 2 - 0.12) * q * q; // accelerating fall
      // a stacked soldier drops to the ground as he topples
      const gy = Math.max(this.ground(c.x, c.z), c.y - 10 * a * a), sink = a > TOPPLE + LIE ? Math.min(1, (a - TOPPLE - LIE) / SINK) * 1.1 : 0;
      T.rotation.set(tip * (c.side ? 0.35 : 1), c.yaw + Math.PI, c.side * tip, 'YXZ');
      T.position.set(c.x, gy - sink, c.z);
      this.reset(k);
      if (N.leg_L) N.leg_L.rotation.x = -0.4 * q;
      if (N.leg_R) N.leg_R.rotation.x = 0.3 * q;
      if (N.arm_free) N.arm_free.rotation.x = -1.4 * q;
      const sc = 1 - 0.55 * Math.min(1, a / (TOPPLE + 0.2));
      this.col.setRGB(sc, sc * 0.92, sc * 0.85);
      this.emit(k, k === this.bkit);
    }
  }

  private poseUnits(f: BossFrame, net: CubeNet, time: number, dt: number) {
    const parade = (net.phase & CubeBit.StateMask) === Cs.Parade;
    this.begin();
    let n = 0, bearerUp = false;
    for (let i = 0; i < f.n && n < MAX; i++) {
      const e = f.units[i];
      let u = this.units.get(e.id);
      if (!u) { u = { x: e.x, y: e.y, z: e.z, yaw: e.yaw, walk: (e.id * 1.7) % 6, seen: time, bearer: false }; this.units.set(e.id, u); }
      const spd = Math.hypot(e.x - u.x, e.z - u.z) / Math.max(1e-3, dt);
      u.x = e.x; u.y = e.y; u.z = e.z; u.yaw = e.yaw; u.seen = time;
      u.walk += dt * Math.min(9, spd * 2.6);
      const sw = Math.min(1, spd / 2);
      // the bearer is the orange stove (once the flying banner has landed on him)
      u.bearer = e.id === net.eid && !this.fly && !!this.bkit;
      const k = u.bearer ? this.bkit! : this.kit!;
      const N = k.N, T = k.T;
      // ---- pose the template for this soldier
      let y = e.y;
      if (e.st === 5) { const q = Math.min(1, e.prog); y += DROP_H * (1 - q * q); }
      const rolling = e.st === 4 && (e.flags & EFlag.Burn) !== 0;
      // per-unit variety (a drafted regiment, not clones): a little yaw / lean / height jitter
      const j1 = hash01(e.id, 1), j2 = hash01(e.id, 2), j3 = hash01(e.id, 3);
      T.position.set(e.x, y, e.z);
      T.rotation.set(rolling ? Math.sin(time * 9 + e.id) * 1.2 : (j1 - 0.5) * 0.06, e.yaw + Math.PI + (j2 - 0.5) * 0.12,
        rolling ? Math.PI * 0.45 : (j3 - 0.5) * 0.05, 'YXZ');
      T.scale.setScalar(0.975 + 0.05 * hash01(e.id, 4));
      this.reset(k);
      const planted = e.st === 3;
      const crouch = e.st === 1 ? 0.18 * (0.5 + 0.5 * Math.sin(time * 14 + e.id * 0.3)) : 0;
      if (N.body) { N.body.position.y -= crouch + (planted ? 0.05 : 0); N.body.rotation.x = planted ? -0.06 : e.st === 2 ? 0.25 : 0; }
      if (N.leg_L) N.leg_L.rotation.x = e.st === 2 ? -0.7 : Math.sin(u.walk) * 0.55 * sw;
      if (N.leg_R) N.leg_R.rotation.x = e.st === 2 ? -0.7 : -Math.sin(u.walk) * 0.55 * sw;
      // parade rest / landed: the door swings open on its hinge (the server tests SLAB_OPEN then)
      if (N.arm_shield) N.arm_shield.rotation.x = e.st === 2 ? -0.5 : 0;
      if (planted) { swing(k.slab, k.slabD, SLAB_SWING); swing(k.door, k.doorD, APRON_SWING); }
      if (N.arm_free) N.arm_free.rotation.x = e.st === 2 ? -1.2 : planted ? 0.25 : Math.sin(u.walk + 1) * 0.15 * sw;
      if (N.head) N.head.rotation.x = e.st === 1 ? -0.08 : 0;
      // ---- tint: hit flash, burning, primed
      const h = f.hitAge(e.id);
      const fl = h < 0.12 ? 1 - h / 0.12 : 0;
      unitTint(e.id, this.col);
      if (e.flags & EFlag.Burn) this.col.setRGB(1.25, 0.82, 0.6);
      if (e.flags & EFlag.Primed) { const q = 0.5 + 0.5 * Math.sin(time * 10); this.col.setRGB(1 + 0.5 * q, 0.75, 1 + 0.6 * q); }
      if (fl > 0) this.col.multiplyScalar(1 + fl * 1.6);
      if (parade && e.st === 3) this.col.multiplyScalar(1.15);
      this.emit(k);
      T.scale.setScalar(1);
      if (u.bearer) {
        bearerUp = true;
        if (this.coreHolder) this.v1.setFromMatrixPosition(this.coreHolder.matrixWorld); else this.v1.set(e.x, y + 0.8, e.z);
        this.glow.position.copy(this.v1);
        this.bearerPos.set(e.x, y, e.z);
      }
      n++;
    }
    this.drawCorpses(time);
    this.end();
    this.glow.visible = bearerUp;
    // forget soldiers that left
    if (this.units.size > n + 8) for (const [id, u] of this.units) if (time - u.seen > 1) this.units.delete(id);
  }

  private reset(k: Kit) {
    for (const [o, r] of k.rest) { o.position.copy(r.p); o.rotation.copy(r.r); }
  }

  private bannerman(f: BossFrame, net: CubeNet, time: number, dt: number) {
    // G7: gold throb on the any-kit weak point (visibility set by poseUnits: only on a drawn bearer)
    const gm = this.glow.material as THREE.SpriteMaterial;
    gm.opacity = 0.55 + 0.4 * Math.sin(time * 6.5);
    // G5: a new bearer → the banner flies from the old one to him, then flashes on arrival
    if (net.eid !== this.lastBearer) {
      if (this.lastBearer && this.bannerFly) this.fly = { from: this.bearerPos.clone(), to: net.eid, t: 0 };
      this.lastBearer = net.eid;
    }
    if (this.fly && this.bannerFly) {
      const to = f.units.slice(0, f.n).find((e) => e.id === this.fly!.to);
      this.fly.t += dt / 0.8;
      const k = Math.min(1, this.fly.t);
      const tx = to ? to.x : this.fly.from.x, ty = to ? to.y : this.fly.from.y, tz = to ? to.z : this.fly.from.z;
      this.bannerFly.visible = true;
      this.bannerFly.position.set(this.fly.from.x + (tx - this.fly.from.x) * k, this.fly.from.y + (ty - this.fly.from.y) * k + 1.1 + Math.sin(k * Math.PI) * 6, this.fly.from.z + (tz - this.fly.from.z) * k);
      this.bannerFly.rotation.set(0, time * 7, Math.sin(k * Math.PI) * 0.6);
      if (k >= 1) { this.fly = null; this.bannerFly.visible = false; this.arriveT = time; this.flashSprite.position.set(tx, ty + 2.2, tz); }
    }
    const fa = time - this.arriveT;
    this.flashSprite.visible = this.arriveT >= 0 && fa < 0.5;
    if (this.flashSprite.visible) {
      (this.flashSprite.material as THREE.SpriteMaterial).opacity = 1 - fa / 0.5;
      this.flashSprite.scale.setScalar(2 + fa * 10);
    }
  }

  bodyDied(type: number, x: number, y: number, z: number, time: number) {
    // the soldier we last drew nearest that spot falls where he stood (his last pose, his facing)
    let best: Unit | null = null, bd = 4;
    for (const u of this.units.values()) { const d = Math.hypot(u.x - x, u.z - z) + Math.abs(u.y - y) * 0.5; if (d < bd) { bd = d; best = u; } }
    if (this.corpses.length >= CORPSES) this.corpses.shift();
    const side = Math.abs((x * 7.31 + z * 3.17) | 0) % 3; // 0 forward onto the slab, ±1 over sideways
    this.corpses.push({ x: best ? best.x : x, y: best ? best.y : y, z: best ? best.z : z, yaw: best ? best.yaw : 0, t0: time, side: side === 2 ? -1 : side, bearer: !!best?.bearer });

    this.ctx.fx()?.robotDeath(this.v1.set(x, y, z), type);
    this.ctx.sfx.play('robot_die', { x, y: y + 1, z }, { pitch: 0.8 });
    this.ctx.sfx.play('debris_clatter', { x, y, z });
  }

  onEvent(kind: number, a: number, b: number, _eid: number, f: BossFrame | null) {
    const sfx = this.ctx.sfx, fx = this.ctx.fx();
    const net = f?.net as CubeNet | undefined;
    const pos = net ? { x: net.cx, y: f!.ground(net.cx, net.cz) + 3, z: net.cz } : undefined;
    switch (kind) {
      case BossBeat.Intro:
        if (a === 1 && pos) {
          sfx.play('explosion', pos, { pitch: 0.55 }); sfx.play('ground_pound', pos);
          fx?.dust(this.v1.set(pos.x, pos.y - 3, pos.z), 1);
          this.ctx.shake(0.7); this.ctx.rumble(0.9, 0.6, 350);
        }
        break;
      case BossBeat.Attack:
        if (a === SLAM) { sfx.play('ground_pound', pos, { pitch: 0.8 }); this.ctx.rumble(0.4, 0.3, 200); }
        else if (b === 1) sfx.play('jump_push', pos, { pitch: 0.6, volume: 1 });
        else { sfx.play('service_klaxon', pos, { pitch: 0.75, volume: 0.8 }); sfx.play('ground_pound', pos, { pitch: 1.2, volume: 0.6 }); }
        break;
      case CubeEv.Slam:
        sfx.play('explosion', pos, { pitch: 0.7 });
        if (pos) fx?.dust(this.v1.set(pos.x, pos.y - 3, pos.z), 1);
        this.ctx.shake(0.45);
        break;
      case CubeEv.Landed:
        sfx.play('explosion', pos, { pitch: 0.65 });
        if (net) for (const c of net.clusters) fx?.dust(this.v1.set(c.x, f!.ground(c.x, c.z), c.z), 1);
        this.ctx.shake(0.6); this.ctx.rumble(0.8, 0.6, 300);
        break;
      case CubeEv.Parade:
        sfx.play('melee_hit', pos, { pitch: 0.55, volume: 0.9 });
        break;
      case CubeEv.Banner: sfx.play('boss_phase', undefined, { pitch: 1.2, volume: 0.7 }); break;
      case CubeEv.Release: sfx.play('boss_phase'); this.ctx.shake(0.4); break;
      case BossBeat.Dead:
        sfx.play('explosion', pos, { pitch: 0.5 }); this.ctx.flash(0.5); this.ctx.shake(0.6);
        break;
    }
  }

  ray(f: BossFrame, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, ap: number, out: BossAim) {
    out.t = -1; out.kind = 0; out.head = false; out.label = '';
    let best = maxT, bp = CPart.None, bearer = false;
    for (let i = 0; i < f.n; i++) {
      const e = f.units[i];
      if (e.st === 5) continue;
      const isB = e.id === f.net.eid;
      unitRaycast(e.x, e.y, e.z, e.yaw, e.st === 3, e.st === 4 && (e.flags & EFlag.Burn) !== 0, isB, ox, oy, oz, dx, dy, dz, best, this.part);
      if (this.part.t >= 0) { best = this.part.t; bp = this.part.part; bearer = isB; out.t = best; }
    }
    if (out.t < 0) return out;
    const pa = PART_ARMOR[bp];
    const av = bp === CPart.Slab && bearer ? 2 : pa.av;
    out.kind = pa.weak ? 3 : av > ap ? 4 : 2;
    out.head = pa.weak;
    out.label = bp === CPart.Slab && bearer ? 'BANNERMAN SLAB · AV2' : PART_NAME[bp];
    return out;
  }

  hud(f: BossFrame, aimLabel: string, o: HudBoss) {
    const net = f.net as CubeNet;
    if (net.phase & CubeBit.Intro) return null;
    let hp = 0;
    for (let i = 0; i < f.n; i++) hp += f.units[i].hp;
    o.name = this.title; o.sub = this.sub;
    o.count = `${net.alive} OF ${net.max}`;
    o.hp = net.max ? hp / net.max : 0;
    o.pips.length = 0;
    o.hyper = false; o.meter = ''; o.crack = 0;
    const st = net.phase & CubeBit.StateMask;
    o.note = net.phase & CubeBit.Released ? '' : st === Cs.Drill ? `DRILL CALL: ${FORMATION_NAMES[net.next]}`
      : st === Cs.Parade ? 'PARADE REST' : net.phase & CubeBit.SlamTell ? 'SYNC SLAM' : '';
    o.warn = net.drop >= DropSt.Tell && net.drop <= DropSt.Hang ? 'LOOK UP' : '';
    o.aimPart = aimLabel;
    return o;
  }

  objective(f: BossFrame | null, o: HudObjective) {
    const net = f ? (f.net as CubeNet) : null;
    o.title = 'CRACK THE FORMATION';
    if (!net) { o.detail = 'SHOOT OVER THE SLABS, OR FROM ABOVE'; return; }
    const st = net.phase & CubeBit.StateMask;
    if (net.phase & CubeBit.Released) { o.title = 'MOP UP THE HOME GUARD'; o.detail = 'THE CUBE IS BROKEN: HIT THEM WHILE THEY REST'; return; }
    if (net.drop >= DropSt.Tell && net.drop <= DropSt.Fall) { o.title = 'CEILING DROP'; o.detail = 'GET OFF THE RINGS'; o.tone = 2; return; }
    if (net.phase & (CubeBit.SlamTell | CubeBit.Ring)) { o.title = 'SYNC SLAM'; o.detail = 'JUMP THE RING'; o.tone = 2; return; }
    if (st === Cs.Parade) { o.detail = 'PARADE REST: SHOOT OVER THE SLABS'; o.tone = 1; return; }
    // the bar already names the call; the plate says what to do about it
    if (st === Cs.Drill) { o.title = `DRILL CALL: ${FORMATION_NAMES[net.next]}`; o.detail = DRILL_ADVICE[net.next] ?? 'GET READY'; o.tone = 2; return; }
    switch (net.atk) {
      case Fm.Encircle: o.detail = 'SURROUNDED: FLY OUT, OR SHOOT THEIR BACKS FROM OUTSIDE'; break;
      case Fm.Grinder: o.detail = 'KEEP CLEAR OF THE SPINNING SLABS'; break;
      case Fm.Gauntlet: o.detail = 'GET OUT OF THE LANE'; break;
      default: o.detail = 'FLANK IT OR FLY OVER: THE FLUE AND THE GOLD PILOT LIGHT ARE WEAK';
    }
  }

  dispose() {
    for (const k of [this.kit, this.bkit]) if (k) for (const p of k.parts) { this.ctx.scene.remove(p.inst); p.inst.dispose(); }
    if (this.bannerFly) this.ctx.scene.remove(this.bannerFly);
    this.ctx.scene.remove(this.glow, this.flashSprite);
    this.ring.dispose();
    for (const s of this.strikes) s.dispose();
    for (const d of this.disposables) d.dispose();
  }
}

function glowTex() {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.3, 'rgba(255,255,255,0.5)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

export const bulwarkClient: BossClientFactory = (ctx) => new CubeClient(ctx);
